import { useState } from "react";
import { Download, FileSpreadsheet, FileText, Table2 } from "lucide-react";
import { downloadUrl } from "@/lib/traffic";
import { Seg } from "@/components/traffic/Widgets";
import { RangePicker, SiteSelect, presetRange } from "@/components/traffic/SiteSelect";
import { cn } from "@/lib/utils";

const TYPES = [
  { id: "traffic", title: "Traffic statistics", text: "Vehicle units by classification (small / medium / large), average and 85th-percentile speed, hourly profile, per-site and per-direction totals, incident counts." },
  { id: "incidents", title: "Incident report", text: "Every radar-detected incident in the period: wrong-way driving, stopped vehicles and slow-downs, with site, lane, time and speed." },
  { id: "radar", title: "Radar availability", text: "Uptime and downtime for each of the 20 radars: availability %, downtime minutes, outages (sender ping + radar server log) and the full outage log for any period." },
];

export default function Reports() {
  const [type, setType] = useState("traffic");
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(presetRange("7d"));
  const [site, setSite] = useState("ALL");
  const [dir, setDir] = useState("ALL");
  const [days, setDays] = useState(30);
  const pick = (p, r) => { setPreset(p); setRange(r); };
  const radarParams = typeof days === "number" ? { days } : { start: range[0], end: range[1] };
  const params = type === "radar" ? radarParams : type === "incidents" ? { start: range[0], end: range[1], site } : { start: range[0], end: range[1], site, dir };
  const path = `/reports/${type}`;
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="eyebrow">Management & statistics exports</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">Reports</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Every report carries the LPT2 logo and the “Powered by Cuvano VIDS” footer. Excel for analysis, PDF for circulation, CSV for other systems.</p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {TYPES.map((t) => (
          <button key={t.id} onClick={() => setType(t.id)} data-testid={`report-type-${t.id}`}
            className={cn("surface flex flex-col items-start p-5 text-left transition-all", type === t.id ? "ring-2 ring-primary/60" : "hover:border-border")}>
            <div className="text-base font-semibold">{t.title}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t.text}</p>
          </button>
        ))}
      </div>
      <section className="surface p-6" data-testid="report-options">
        <h2 className="text-base font-semibold">Options</h2>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {type === "radar" ? (
            <>
              <Seg value={days} onChange={(v) => { setDays(v); if (v === "range") pick("last_month", presetRange("last_month")); }}
                options={[{ value: 1, label: "24 h" }, { value: 7, label: "7 days" }, { value: 30, label: "30 days" }, { value: 90, label: "90 days" }, { value: "range", label: "Date range" }]} testId="report-radar-days" />
              {days === "range" && <RangePicker preset={preset} range={range} onChange={pick} testId="report-radar-range" />}
            </>
          ) : (
            <>
              <RangePicker preset={preset} range={range} onChange={pick} testId="report-preset" />
              <SiteSelect value={site} onChange={setSite} testId="report-site" />
              {type === "traffic" && <Seg value={dir} onChange={setDir} options={[{ value: "ALL", label: "Both" }, { value: "NB", label: "NB" }, { value: "SB", label: "SB" }]} testId="report-dir" />}
            </>
          )}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[["xlsx", "Excel workbook", FileSpreadsheet, "export-excel-button"], ["pdf", "PDF document", FileText, "export-pdf-button"], ["csv", "CSV data", Table2, "export-csv-button"]].map(([fmt, label, Icon, tid]) => (
            <a key={fmt} href={downloadUrl(path, { ...params, format: fmt })} data-testid={tid}
              className="group flex items-center gap-3 rounded-2xl border border-border/70 px-4 py-3 transition-colors hover:bg-secondary/60">
              <span className="rounded-xl bg-secondary p-2 group-hover:bg-background"><Icon className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block text-sm font-medium">{label}</span><span className="block text-xs text-muted-foreground">.{fmt}</span></span>
              <Download className="h-4 w-4 text-muted-foreground" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
